import {
  IBankApi,
  IBankInfo,
  IResponsePlan,
  IResponsePlanForm,
  IResponsePlanInfos,
  IResponsePlanAggregation,
} from 'models/response.plan.model'
import api from './api'
import { getRegisterIdFromStore } from 'util/storeHelpers'

const formBankToApiBanks = (banks: Array<IBankInfo>): Array<IBankApi> => {
  const newBanks = banks.map(bank => {
    const newBank = {
      name: bank.name,
      contact: [
        {
          phone: bank.phone,
          email: bank.email,
        },
      ],
    }
    return newBank
  })
  return newBanks
}

// TODO change backend to contacts instead contact
const apiBankToApiBank = (banks: Array<IBankApi>): Array<IBankApi> => {
  const newBanks = banks.map(bank => {
    const newBank = {
      name: bank.name,
      contact: bank.contact?.map(bankInterator => {
        return {
          phone: bankInterator.phone,
          email: bankInterator.email,
        }
      }),
    }
    return newBank
  })
  return newBanks
}

const removeInvalidEntriesInResponsePlanInfos = (
  responsePlanInfos: Array<IResponsePlanInfos>,
): Array<IResponsePlanInfos> => {
  return responsePlanInfos.map(planInfos => {
    delete planInfos._id
    return planInfos
  })
}

const removeInvalidEntriesForApi = (responsePlan: IResponsePlan): IResponsePlan => {
  responsePlan.bank = apiBankToApiBank(responsePlan.bank)
  responsePlan.familyContacts = removeInvalidEntriesInResponsePlanInfos(responsePlan.familyContacts)
  responsePlan.professionalContacts = removeInvalidEntriesInResponsePlanInfos(
    responsePlan.professionalContacts,
  )
  return responsePlan
}

const convertResponsePlanObjectToApi = (responsePlan: IResponsePlan): IResponsePlan => {
  let newResponsePlan: IResponsePlan = {
    savedCode: responsePlan.savedCode,
    familyContacts: responsePlan.familyContacts,
    professionalContacts: responsePlan.professionalContacts,
    bank: responsePlan.bank,
    checks: responsePlan.checks,
  }

  if (responsePlan.checks.status === 'INITIAL') delete newResponsePlan.checks.status

  newResponsePlan = removeInvalidEntriesForApi(newResponsePlan)

  return newResponsePlan
}

const createResponsePlan = async (values: IResponsePlanForm) => {
  const registerId = getRegisterIdFromStore()

  // Defensive check: Ensure registerId exists before making the request
  if (!registerId) {
    throw new Error(
      'User registration ID is required to create a response plan. Please ensure you are logged in.',
    )
  }

  const body = {
    familyContacts: values.familyContacts,
    professionalContacts: values.professionalContacts,
    bank: [...apiBankToApiBank(values.banks), ...formBankToApiBanks(values.banksManually)],
  }

  const { data } = await api.post(`/v1/responsePlan`, body, {
    headers: {
      registerId,
    },
  })
  return data
}

const getAllBanks = async (): Promise<Array<IBankApi>> => {
  const { data } = await api.get(`/v1/banks`)
  return data
}

const getResponsePlanLoggedOut = async (accessCode: string): Promise<IResponsePlan> => {
  const { data } = await api.get(`/v1/responsePlan/${accessCode}`)
  return data
}

const updateResponsePlan = async (responsePlan: IResponsePlan): Promise<IResponsePlan> => {
  const registerId = getRegisterIdFromStore()
  const responsePlanToSend = convertResponsePlanObjectToApi(responsePlan)
  const { data } = await api.put(`/v1/responsePlan/${responsePlan.code}`, responsePlanToSend, {
    headers: {
      registerId,
    },
  })
  return data
}

const getResponsePlan = async (): Promise<IResponsePlan> => {
  const registerId = getRegisterIdFromStore()

  const { data } = await api.get(`/v1/responsePlan`, {
    headers: {
      registerId,
    },
  })
  return data
}

const getResponsePlanAggregation = async (): Promise<IResponsePlanAggregation> => {
  const registerId = getRegisterIdFromStore()

  const { data } = await api.get(`/v1/responsePlan/manager/aggregation`, {
    headers: {
      registerId,
    },
  })
  return data
}

const sendNotification = async (type: string, message: string, accessCode: string) => {
  const { data } = await api.post(`/v1/responsePlan/notifications/${accessCode}`, {
    contact: type,
    message: message,
  })
  return data
}

export default {
  createResponsePlan,
  getAllBanks,
  getResponsePlanLoggedOut,
  getResponsePlan,
  updateResponsePlan,
  getResponsePlanAggregation,
  sendNotification,
}
